import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './SelfInfo.css';

const API_BASE = process.env.REACT_APP_API_BASE;

function SelfInfo({ userData, memberDetails: propMemberDetails }) {
    const [memberDetails, setMemberDetails] = useState(propMemberDetails || null);
    const [loading, setLoading] = useState(!propMemberDetails);
    const [error, setError] = useState(null);

    const accountCreated = userData?.id ? new Date(Number((BigInt(userData.id) >> 22n) + 1420070400000n)) : null;

    useEffect(() => {
        if (propMemberDetails) {
            setMemberDetails(propMemberDetails);
            setLoading(false);
            return;
        }

        const fetchMemberDetails = async () => {
            const token = localStorage.getItem('discord_token');
            if (!token || !userData?.id) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const response = await axios.get(`${API_BASE}/api/member-details/${userData.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setMemberDetails(response.data);
            } catch (err) {
                console.error('Error fetching member details:', err);
                setError('Fehler beim Laden der Benutzerdaten');
            } finally {
                setLoading(false);
            }
        };

        fetchMemberDetails();
    }, [userData, propMemberDetails]);

    if (loading) {
        return (
            <div className="dashboard-container">
                <div className="widget-card selfinfo-feedback-card">
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <h2>Self Info wird geladen...</h2>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-container">
                <div className="widget-card selfinfo-feedback-card">
                    <div className="access-denied">
                        <div className="access-denied-icon">⚠️</div>
                        <h2>Fehler</h2>
                        <p>{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            {/* Hero Section */}
            <div className="dashboard-hero">
                <div className="hero-content">
                    <div className="hero-greeting">
                        <div className="hero-icon">👤</div>
                        <div>
                            <h1 className="hero-title">Self Info</h1>
                            <p className="hero-subtitle">Alle Daten von deinem Discord Account</p>
                        </div>
                    </div>
                </div>
                <div className="hero-stats">
                    <div className="stat-item">
                        <div className="stat-value">
                            {accountCreated ? Math.floor((Date.now() - accountCreated.getTime()) / (1000 * 60 * 60 * 24 * 365)) : '?'}
                        </div>
                        <div className="stat-label">Jahre alt</div>
                    </div>
                    <div className="stat-item">
                        <div className="stat-value">{memberDetails?.roles?.length || 0}</div>
                        <div className="stat-label">Rollen</div>
                    </div>
                    <div className="stat-item">
                        <div className="stat-value">{memberDetails?.permissions?.length || 0}</div>
                        <div className="stat-label">Berechtigungen</div>
                    </div>
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="selfinfo-widgets">
                {/* User Profile Card */}
                <div className="widget-card highlight">
                    <div className="widget-header">
                        <div className="widget-title">
                            <div className="widget-icon">👤</div>
                            Benutzerprofil
                        </div>
                        <span className="widget-badge new">Discord</span>
                    </div>
                    <div className="widget-content">
                        <div className="selfinfo-profile">
                            <div className="selfinfo-profile-avatar">
                                <img src={userData.avatarUrl} alt="Avatar" />
                                <div className="status-dot"></div>
                            </div>
                            <div className="selfinfo-profile-details">
                                <h3 className="selfinfo-profile-name">
                                    {userData.global_name || userData.username}
                                </h3>
                                <p className="selfinfo-profile-username">@{userData.username}</p>
                                {userData.discriminator && userData.discriminator !== '0' && (
                                    <p className="selfinfo-profile-discriminator">#{userData.discriminator}</p>
                                )}
                                <p className="selfinfo-profile-email">{userData.email}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Account Information Card */}
                <div className="widget-card">
                    <div className="widget-header">
                        <div className="widget-title">
                            <div className="widget-icon selfinfo-widget-icon-yellow">📅</div>
                            Account Informationen
                        </div>
                    </div>
                    <div className="widget-content">
                        <div className="selfinfo-server-grid">
                            <div className="selfinfo-info-item selfinfo-info-item-yellow">
                                <div className="info-label">
                                    <svg className="selfinfo-svg-yellow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                        <line x1="16" y1="2" x2="16" y2="6"></line>
                                        <line x1="8" y1="2" x2="8" y2="6"></line>
                                        <line x1="3" y1="10" x2="21" y2="10"></line>
                                    </svg>
                                    Erstellt
                                </div>
                                <div className="info-value selfinfo-info-value-compact">
                                    {accountCreated ? accountCreated.toLocaleDateString('de-DE', {
                                        year: 'numeric', month: 'short', day: 'numeric'
                                    }) : '?'}
                                </div>
                            </div>
                            <div className="selfinfo-info-item selfinfo-info-item-green">
                                <div className="info-label">
                                    <svg className="selfinfo-svg-green" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <polyline points="12 6 12 12 16 14"></polyline>
                                    </svg>
                                    Alter
                                </div>
                                <div className="info-value selfinfo-info-value-compact">
                                    {accountCreated ? Math.floor((Date.now() - accountCreated.getTime()) / (1000 * 60 * 60 * 24 * 365)) + ' Jahre' : '?'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Server Information Card */}
                {memberDetails && (
                    <>
                        <div className="widget-card">
                            <div className="widget-header">
                                <div className="widget-title">
                                    <div className="widget-icon selfinfo-widget-icon-blue">🖥️</div>
                                    Server Informationen
                                </div>
                            </div>
                            <div className="widget-content">
                                <div className="selfinfo-server-grid">
                                    <div className="selfinfo-info-item selfinfo-info-item-blue">
                                        <div className="info-label">
                                            <svg className="selfinfo-svg-blue" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                                                <polyline points="10 17 15 12 10 7"></polyline>
                                                <line x1="15" y1="12" x2="3" y2="12"></line>
                                            </svg>
                                            Beigetreten
                                        </div>
                                        <div className="info-value selfinfo-info-value-small">
                                            {new Date(memberDetails.joinedAt).toLocaleDateString('de-DE', {
                                                day: '2-digit', month: 'short', year: 'numeric'
                                            })}
                                        </div>
                                    </div>
                                    {memberDetails.teamJoinDate && (
                                        <div className="selfinfo-info-item selfinfo-info-item-purple">
                                            <div className="info-label">
                                                <svg className="selfinfo-svg-purple" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4 "></path>
                                                    <circle cx="9" cy="7" r="4"></circle>
                                                </svg>
                                                Team
                                            </div>
                                            <div className="info-value selfinfo-info-value-small">
                                                {new Date(memberDetails.teamJoinDate).toLocaleDateString('de-DE', {
                                                    day: '2-digit', month: 'short', year: 'numeric'
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {memberDetails.in_voice && (
                            <div className="widget-card success">
                                <div className="widget-header">
                                    <div className="widget-title">
                                        <div className="widget-icon selfinfo-widget-icon-green">🔊</div>
                                        Voice Channel
                                    </div>
                                </div>
                                <div className="widget-content">
                                    <div className="selfinfo-server-grid">
                                        <div className="selfinfo-info-item selfinfo-info-item-success">
                                            <div className="info-label">
                                                <svg className="selfinfo-svg-green" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                                                </svg>
                                                Channel
                                            </div>
                                            <div className="info-value selfinfo-info-value-small selfinfo-info-value-voice">
                                                {memberDetails.voice_channel_name}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="widget-card widget-full">
                            <div className="widget-header">
                                <div className="widget-title">
                                    <div className="widget-icon selfinfo-widget-icon-purple">🎭</div>
                                    Rollen ({memberDetails.roles?.length || 0})
                                </div>
                            </div>
                            <div className="widget-content">
                                <div className="selfinfo-roles">
                                    {memberDetails.roles?.map(role => (
                                        <span
                                            key={role.id}
                                            className="selfinfo-role"
                                            style={{
                                                backgroundColor: role.color !== '#000000' ? role.color + '40' : 'rgba(51, 51, 51, 0.5)',
                                                borderColor: role.color !== '#000000' ? role.color : '#333',
                                                color: role.color !== '#000000' ? role.color : '#fff'
                                            }}
                                            title={`${role.name} (Position: ${role.position})`}
                                        >
                                            {role.name}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="widget-card widget-full">
                            <div className="widget-header">
                                <div className="widget-title">
                                    <div className="widget-icon selfinfo-widget-icon-cyan">🔑</div>
                                    Berechtigungen ({memberDetails.permissions?.length || 0})
                                </div>
                            </div>
                            <div className="widget-content">
                                <div className="selfinfo-permissions">
                                    {memberDetails.permissions?.map(permission => (
                                        <span key={permission} className="selfinfo-permission">
                                            {permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {memberDetails.activity && memberDetails.activity.length > 0 && (
                            <div className="widget-card widget-full">
                                <div className="widget-header">
                                    <div className="widget-title">
                                        <div className="widget-icon selfinfo-widget-icon-green">🎮</div>
                                        Aktivitäten ({memberDetails.activity?.length || 0})
                                    </div>
                                </div>
                                <div className="widget-content">
                                    <div className="selfinfo-activities">
                                        {memberDetails.activity.map((activity, index) => (
                                            <div key={index} className="selfinfo-activity">
                                                <div className="selfinfo-activity-header">
                                                    <span className="selfinfo-activity-icon">
                                                        {activity.type === 0 && '🎮'}
                                                        {activity.type === 1 && '📺'}
                                                        {activity.type === 2 && '🎵'}
                                                        {activity.type === 3 && '📺'}
                                                        {activity.type === 4 && '📝'}
                                                        {activity.type === 5 && '🏆'}
                                                        {!activity.type && '🎮'}
                                                    </span>
                                                    <span className="selfinfo-activity-name">{activity.name}</span>
                                                </div>
                                                {activity.details && (
                                                    <div className="selfinfo-activity-details">{activity.details}</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default SelfInfo;
